import {
	Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
	FormControl, InputLabel, ListItemText, MenuItem, Select, Stack, TextField
} from "@mui/material";
import { addBill, getByClientBill } from "api/layer";
import { AppAction, AppContext } from "helpers/context";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./add-modal.module.scss";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import "dayjs/locale/en"; // 导入中文语言包
import "dayjs/locale/zh"; // 导入中文语言包

interface Props {
	open: boolean;
	onClose: () => void;
	onSave: () => void;
	data?: FormData;
	clientData: any[];
}

interface FormData {
	id?: string;
	billCode: string;
	billName: string;
	clientId: string;
	keyId: string;
	keyCode?: string;
	startTime: any;
	endTime: any;
}

function AddModal(props: Props) {
	const [t] = useTranslation();
	const [context, dispatch] = useContext(AppContext);
	const [keyList, setKeyList] = useState<any[]>([]);
	const [formData, setFormData] = useState<FormData>(
		props.data
			? {
				...props.data,
				startTime: dayjs(props.data.startTime),
				endTime: dayjs(props.data.endTime)
			}
			: {
				billCode: "",
				billName: "",
				clientId: "",
				keyId: "",
				startTime: null,
				endTime: null,
			}
	);
	const [isError, setIsError] = useState(false);

	const handleGetKeyList = (id: any) => {
		getByClientBill(id).then((res: any) => {
			if (res && res.code === 200) {
				let data = props.data ? [{
					id: props.data.keyId,
					keyCode: props.data.keyCode
				},
				...res.data
				] : (res.data || []);

				let filterIdData = data.filter((item: any, index: number) => {
					return data.findIndex((itm: any) => itm.id === item.id) === index
				})

				setKeyList(filterIdData);
			} else {
				setKeyList([]);
			}
		});
	}

	const handleChange = (event: any) => {
		const { name, value, type, checked } = event.target;
		const val = type === "checkbox" ? checked : value;
		if (name === "clientId") {
			handleGetKeyList(value)
		}
		setFormData((prevData) => ({ ...prevData, [name]: val }));
	};

	const close = () => {
		props.onClose();
	};

	const handleSave = () => {
		const isStartTimeValid = dayjs(formData.startTime).isValid();
		const isEndTimeValid = dayjs(formData.endTime).isValid();

		if (!formData.billCode || !formData.billName || !formData.clientId || !formData.keyId || !formData.startTime || !formData.endTime || !isStartTimeValid || !isEndTimeValid) {
			setIsError(true);
			return;
		}
		setIsError(false);

		let data = {
			...formData,
			startTime: dayjs(formData.startTime).format("YYYY-MM-DD"),
			endTime: dayjs(formData.endTime).format("YYYY-MM-DD"),
		};

		if (props.data) {
			addBill(data)
				.then((res: any) => {
					if (res && res.code === 200) {
						props.onClose();
						dispatch({
							type: AppAction.addSnackBarParams,
							payload: {
								title: "editBill",
								message: "editBillSuccess",
							},
						});
					} else {
						dispatch({
							type: AppAction.addSnackBarParams,
							payload: {
								title: "editBill",
								titleIconColor: "yellow",
								titleIcon: "warning",
								resMessage: res.message,
							},
						});
					}
				})
				.finally(() => {
					props.onSave();
				});
		} else {
			addBill(data)
				.then((res: any) => {
					if (res && res.code === 200) {
						props.onClose();
						dispatch({
							type: AppAction.addSnackBarParams,
							payload: {
								title: "addBill",
								message: "addBillSuccess",
							},
						});
					} else {
						dispatch({
							type: AppAction.addSnackBarParams,
							payload: {
								title: "addBill",
								titleIconColor: "yellow",
								titleIcon: "warning",
								resMessage: res.message,
							},
						});
					}
				})
				.finally(() => {
					props.onSave();
				});
		}
	};

	useEffect(() => {
		if (props.data) {
			handleGetKeyList(props.data.clientId)
		}
	}, [])

	return (
		<Dialog
			open={props.open}
			onClose={close}
		>
			<DialogTitle
				sx={{
					fontWeight: 600,
					fontSize: "16px",
					opacity: 0.79,
					color: "#333",
					width: "400px",
				}}
			>
				{props.data ? t("userSystem.editBill") : t("userSystem.addBill")}
			</DialogTitle>
			<DialogContent sx={{ paddingTop: "10px!important" }}>
				<Stack spacing={{ xy: 1, sm: 2 }}>
					<TextField
						required
						name="billCode"
						value={formData.billCode}
						error={isError && !formData.billCode}
						onChange={handleChange}
						label={t("userSystem.billCode")}
						variant="outlined"
						color="success"
					/>
					<TextField
						required
						name="billName"
						value={formData.billName}
						error={isError && !formData.billName}
						onChange={handleChange}
						label={t("userSystem.billName")}
						variant="outlined"
						color="success"
					/>

					<FormControl fullWidth error={isError && !formData.clientId}>
						<InputLabel color="success" required>{t("userSystem.clientName")}</InputLabel>
						<Select
							name="clientId"
							value={formData.clientId}
							onChange={handleChange}
							label={t("userSystem.clientName")}
							color="success"
							MenuProps={{
								PaperProps: {
									style: {
										maxHeight: 350,
									},
								},
							}}
						>
							{props.clientData.map((item) => (<MenuItem value={item.id} key={item.id}>{item.clientName}</MenuItem>))}
						</Select>
					</FormControl>

					{formData.clientId && <FormControl fullWidth error={isError && !formData.keyId}>
						<InputLabel color="success" required>{t("userSystem.keyCode")}</InputLabel>
						<Select
							name="keyId"
							value={formData.keyId}
							onChange={handleChange}
							label={t("userSystem.keyCode")}
							color="success"
							MenuProps={{
								PaperProps: {
									style: {
										maxHeight: 350,
									},
								},
							}}
						>
							{keyList.map((item) => (<MenuItem value={item.id} key={item.id}>{item.keyCode}</MenuItem>))}
						</Select>
					</FormControl>}

					<LocalizationProvider
						dateAdapter={AdapterDayjs}
						adapterLocale={context.language === "cn" ? "zh" : "en"}

					>
						<DatePicker
							label={t("userSystem.startDate") + " *"}
							value={formData.startTime}
							format="YYYY-MM-DD"
							onChange={(event) => {
								handleChange({ target: { name: "startTime", value: event } });
							}}
							maxDate={formData.endTime}
							sx={{
								"& .MuiInputLabel-root": {
									color: isError && !formData.startTime ? "#d32f2f" : "",
								},
								"& .MuiPickersOutlinedInput-notchedOutline,& .Mui-focused:not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline": {
									borderColor: isError && !formData.startTime ? "#d32f2f!important" : "",
								},
								"& .MuiInputLabel-root.Mui-focused": {
									color: isError && !formData.startTime ? "#d32f2f" : "green",
								},
								"& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
									borderColor: isError && !formData.startTime ? "#d32f2f" : "green",
								},
							}}
						/>
						<DatePicker
							label={t("userSystem.endDate") + " *"}
							value={formData.endTime}
							format="YYYY-MM-DD"
							onChange={(event) => {
								handleChange({ target: { name: "endTime", value: event } });
							}}
							minDate={formData.startTime}
							slotProps={{
								textField: {
									color: 'success',
									variant: 'outlined',
								},
							}}
							sx={{
								"& .MuiInputLabel-root": {
									color: isError && !formData.endTime ? "#d32f2f" : "",
								},
								"& .MuiPickersOutlinedInput-notchedOutline,& .Mui-focused:not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline": {
									borderColor: isError && !formData.endTime ? "#d32f2f!important" : "",
								},
								"& .MuiInputLabel-root.Mui-focused": {
									color: isError && !formData.endTime ? "#d32f2f" : "green",
								},
								"& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
									borderColor: isError && !formData.endTime ? "#d32f2f" : "green",
								},
							}}
						/>
					</LocalizationProvider>

				</Stack>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={close}
					color="success"
				>
					{t("buttons.cancel")}
				</Button>
				<Button
					onClick={handleSave}
					color="success"
					variant="contained"
				>
					{t("buttons.save")}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export default AddModal;
